import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/basedata/apiCall';
import {
  getOnboardingGuest,
  updateOnboardingGuestCompanions,
} from '@/helpers/onboardingGuest';
import { getOnboardingSessionId } from '@/helpers/onboardingSession';
import { getOnboardingEventContext } from '@/helpers/tenantBranding';
import { companionCanSelfOnboard } from '@/Pages/Companions/utils/companionAge';
import { readCompanionPhoto } from '@/Pages/Companions/utils/companionPhoto';
import { buildCompanionShareText } from '@/Pages/Companions/utils/companionShareText';
import { shareInviteLink, type ShareResult } from '@/Pages/Companions/utils/shareInviteLink';
import { successNavigationOptions } from '@/Pages/Success/types/successLocationState';
import { stripCpfDigits } from '@/Pages/Signup/utils/cpfMask';
import type { CompanionItem, CompanionsProgress } from '@/types/guestOnboarding';

const MIN_DOCUMENT_DIGITS = 11;

/** Adicional na lista + o link já emitido nesta visita (o token só sai uma vez). */
export type CompanionRow = CompanionItem & { inviteUrl: string | null };

export type UseCompanionsResult = {
  progress: CompanionsProgress;
  rows: CompanionRow[];
  /** Lista ainda chegando: a tela NÃO decide o layout com o progresso velho. */
  isLoading: boolean;
  /** Lista não carregou (mensagem do service quando houver) — a tela oferece retry. */
  loadError: string | null;
  reload: () => void;
  fullName: string;
  birthDate: string;
  document: string;
  email: string;
  nationality: string;
  /** Acompanhante em edição (null = o formulário está criando um novo). */
  editingId: string | null;
  /** CPF mascarado de quem está sendo editado — o campo entra vazio. */
  editingDocumentMasked: string | null;
  /** Exclusão em andamento (id da linha). */
  removingId: string | null;
  hasPhoto: boolean;
  consentConfirmed: boolean;
  /** Adicional com mais de 16 anos pode fazer o próprio cadastro. */
  canSelfOnboard: boolean;
  photoError: 'too_large' | 'invalid_type' | null;
  submitError: string | null;
  shareFeedback: ShareResult | null;
  isSubmitting: boolean;
  canSubmitWithPhoto: boolean;
  canSubmitForSelfOnboarding: boolean;
  /** Só dá para cadastrar acompanhante novo enquanto sobrar vaga do organizador. */
  hasOpenSlot: boolean;
  /** Acompanhante em que a foto está sendo enviada agora (linha da lista). */
  uploadingFor: string | null;
  setFullName: (value: string) => void;
  setBirthDate: (value: string) => void;
  setDocument: (value: string) => void;
  setEmail: (value: string) => void;
  setNationality: (value: string) => void;
  setConsentConfirmed: (value: boolean) => void;
  /** Abre o formulário com os dados da linha (edição). */
  startEdit: (row: CompanionRow) => void;
  cancelEdit: () => void;
  saveEdit: () => Promise<void>;
  /** Exclui (cancela) o acompanhante: a vaga volta e a face perde valor. */
  removeCompanion: (companionId: string) => Promise<void>;
  pickPhoto: (file: File | null) => void;
  submitWithPhoto: () => Promise<void>;
  submitForSelfOnboarding: () => Promise<void>;
  share: (companion: CompanionRow) => Promise<void>;
  /** Envia a foto de um acompanhante que JÁ existe (criado pelo painel). */
  sendPhotoFor: (companionId: string, file: File | null) => Promise<void>;
  finish: () => void;
};

/**
 * Cadastro dos ADICIONAIS pelo titular, logo depois do cadastro dele.
 *
 * Dois caminhos por adicional:
 *  - **foto agora**: o titular captura/envia a foto e o service valida o rosto
 *    e gera o template facial antes de dar OK — o adicional já fica pronto;
 *  - **o adicional se cadastra** (só acima de 16 anos): com CPF ele se
 *    identifica pela URL do evento; sem CPF sai um LINK ÚNICO para o titular
 *    compartilhar. Mesma regra do convite de titular no painel.
 *
 * Pulável: `finish` leva ao /success e o titular volta pelo mesmo link.
 */
export function useCompanions(): UseCompanionsResult {
  const navigate = useNavigate();
  const guest = getOnboardingGuest();

  const [progress, setProgress] = useState<CompanionsProgress>(
    guest?.companions ?? { declared: 0, captured: 0, pending: 0, openSlots: 0 },
  );
  const [rows, setRows] = useState<CompanionRow[]>([]);
  const [fullName, setFullName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [document, setDocumentState] = useState('');
  const [email, setEmail] = useState('');
  const [nationality, setNationality] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDocumentMasked, setEditingDocumentMasked] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [base64Image, setBase64Image] = useState<string | null>(null);
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [photoError, setPhotoError] = useState<'too_large' | 'invalid_type' | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [shareFeedback, setShareFeedback] = useState<ShareResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadSeq = useRef(0);

  // Sem contexto de convidado (entrou direto na URL) → identifica primeiro.
  useEffect(() => {
    if (guest === null) {
      navigate('/identify', { replace: true });
    }
  }, [guest, navigate]);

  /**
   * Reentrada: o titular volta pelo mesmo link e vê o que já cadastrou.
   *
   * A lista NÃO é conveniência — é ela que diz quem ocupa cada vaga. Com
   * `openSlots` zerado (vaga tomada por um acompanhante ainda sem biometria,
   * inclusive os que o painel criou) o único caminho para resolver é a linha
   * dele na lista: sem a lista a tela virava um beco com "deixar para depois".
   * Por isso a falha vira estado visível (`loadError`) com "tentar de novo",
   * e o progresso velho da sessão não decide layout nenhum enquanto carrega.
   */
  const reload = useCallback((): void => {
    const sessionId = getOnboardingSessionId();
    if (sessionId === null) {
      setIsLoading(false);
      return;
    }

    const seq = loadSeq.current + 1;
    loadSeq.current = seq;
    setIsLoading(true);
    setLoadError(null);

    void api.onboarding
      .listCompanions({ sessionId })
      .then((response) => {
        if (seq !== loadSeq.current) return;
        setRows(response.items.map((item) => ({ ...item, inviteUrl: null })));
        setProgress(response.companions);
        updateOnboardingGuestCompanions(response.companions);
        setIsLoading(false);
      })
      .catch((error: unknown) => {
        if (seq !== loadSeq.current) return;
        setLoadError(
          error instanceof ApiError && error.message.trim() !== ''
            ? error.message
            : 'Não foi possível carregar os acompanhantes agora.',
        );
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (getOnboardingGuest() === null) return;
    reload();
  }, [reload]);

  const setDocument = useCallback((value: string) => {
    setDocumentState(stripCpfDigits(value).slice(0, 11));
  }, []);

  const resetForm = useCallback(() => {
    setFullName('');
    setBirthDate('');
    setDocumentState('');
    setEmail('');
    setNationality('');
    setBase64Image(null);
    setConsentConfirmed(false);
    setEditingId(null);
    setEditingDocumentMasked(null);
  }, []);

  const pickPhoto = useCallback((file: File | null) => {
    setPhotoError(null);
    setBase64Image(null);
    if (file === null) return;
    void readCompanionPhoto(file).then((result) => {
      if (result === 'too_large' || result === 'invalid_type') {
        setPhotoError(result);
        return;
      }
      setBase64Image(result);
    });
  }, []);

  const canSelfOnboard = birthDate !== '' && companionCanSelfOnboard(birthDate);
  const hasName = fullName.trim().length >= 2;
  // Criar acompanhante novo consome VAGA (openSlots). Já os que existem sem
  // biometria — inclusive os criados pelo painel — continuam podendo receber
  // foto/link mesmo com openSlots zerado.
  const hasOpenSlot = progress.openSlots > 0;

  const canSubmitWithPhoto = hasOpenSlot && hasName && base64Image !== null && consentConfirmed;
  const canSubmitForSelfOnboarding = hasOpenSlot && hasName && canSelfOnboard;

  const register = useCallback(
    async (withPhoto: boolean): Promise<void> => {
      const sessionId = getOnboardingSessionId();
      if (sessionId === null) {
        navigate('/terms', { replace: true });
        return;
      }
      const documentDigits = stripCpfDigits(document);

      setIsSubmitting(true);
      setSubmitError(null);
      setShareFeedback(null);

      try {
        const response = await api.onboarding.registerCompanion({
          sessionId,
          fullName: fullName.trim(),
          ...(birthDate !== '' ? { birthDate } : {}),
          ...(documentDigits.length >= MIN_DOCUMENT_DIGITS ? { document: documentDigits } : {}),
          ...(email.trim() !== '' ? { email: email.trim() } : {}),
          ...(nationality.trim() !== '' ? { nationality: nationality.trim() } : {}),
          ...(withPhoto && base64Image !== null
            ? { base64Image, consentConfirmed: true }
            : {}),
        });

        setRows((current) => [...current, response.companion]);
        setProgress(response.companions);
        updateOnboardingGuestCompanions(response.companions);
        resetForm();
      } catch (error) {
        setSubmitError(
          error instanceof ApiError
            ? error.message
            : 'Não foi possível cadastrar o acompanhante. Tente novamente.',
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [base64Image, birthDate, document, email, fullName, nationality, navigate, resetForm],
  );

  const submitWithPhoto = useCallback(() => register(true), [register]);
  const submitForSelfOnboarding = useCallback(() => register(false), [register]);

  /**
   * Edição: o formulário é o MESMO do cadastro novo, só que preenchido com o
   * que já existe. O CPF entra vazio de propósito — o service só devolve ele
   * mascarado, então digitar é a única forma de trocar (deixar em branco mantém).
   */
  const startEdit = useCallback((row: CompanionRow) => {
    setSubmitError(null);
    setShareFeedback(null);
    setPhotoError(null);
    setBase64Image(null);
    setConsentConfirmed(false);
    setEditingId(row.id);
    setEditingDocumentMasked(row.documentMasked);
    setFullName(row.fullName);
    setBirthDate(row.birthDate ?? '');
    setDocumentState('');
    setEmail(row.email ?? '');
    setNationality(row.nationality ?? '');
  }, []);

  const cancelEdit = useCallback(() => {
    setSubmitError(null);
    resetForm();
  }, [resetForm]);

  const saveEdit = useCallback(async (): Promise<void> => {
    const sessionId = getOnboardingSessionId();
    if (sessionId === null || editingId === null) return;
    const documentDigits = stripCpfDigits(document);

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = await api.onboarding.updateCompanion(editingId, {
        sessionId,
        fullName: fullName.trim(),
        ...(birthDate !== '' ? { birthDate } : {}),
        // Campo vazio NÃO apaga o CPF gravado: sem o dígito digitado o service
        // nem recebe o campo (o titular só vê a máscara).
        ...(documentDigits.length >= MIN_DOCUMENT_DIGITS ? { document: documentDigits } : {}),
        email: email.trim(),
        nationality: nationality.trim(),
      });

      setRows((current) =>
        current.map((row) =>
          row.id === editingId ? { ...response.companion, inviteUrl: row.inviteUrl } : row,
        ),
      );
      setProgress(response.companions);
      updateOnboardingGuestCompanions(response.companions);
      resetForm();
    } catch (error) {
      setSubmitError(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível salvar as alterações. Tente novamente.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [birthDate, document, editingId, email, fullName, nationality, resetForm]);

  /** Excluir = cancelar no service: a vaga volta e a face perde valor no portão. */
  const removeCompanion = useCallback(
    async (companionId: string): Promise<void> => {
      const sessionId = getOnboardingSessionId();
      if (sessionId === null) return;

      setRemovingId(companionId);
      setSubmitError(null);
      setShareFeedback(null);
      try {
        const response = await api.onboarding.cancelCompanion(companionId, { sessionId });
        setRows((current) => current.filter((row) => row.id !== companionId));
        setProgress(response.companions);
        updateOnboardingGuestCompanions(response.companions);
        setEditingId((current) => (current === companionId ? null : current));
      } catch (error) {
        setSubmitError(
          error instanceof ApiError
            ? error.message
            : 'Não foi possível excluir o acompanhante. Tente novamente.',
        );
      } finally {
        setRemovingId(null);
      }
    },
    [],
  );

  /** Link já em mãos é compartilhado direto; senão o service emite um novo. */
  const share = useCallback(async (companion: CompanionRow): Promise<void> => {
    const sessionId = getOnboardingSessionId();
    setShareFeedback(null);

    let url = companion.inviteUrl;
    if (url === null) {
      if (sessionId === null) return;
      try {
        const response = await api.onboarding.companionInviteLink(companion.id, { sessionId });
        url = response.url;
        setRows((current) =>
          current.map((row) => (row.id === companion.id ? { ...row, inviteUrl: url } : row)),
        );
      } catch {
        setShareFeedback('failed');
        return;
      }
    }

    // Quem compartilha é o titular desta sessão: o nome dele e o do evento
    // personalizam a mensagem (com fallback quando algum falta).
    const text = buildCompanionShareText({
      companionName: companion.fullName,
      titularName: getOnboardingGuest()?.fullName ?? null,
      eventName: getOnboardingEventContext()?.name ?? null,
    });
    setShareFeedback(await shareInviteLink(url, text));
  }, []);

  /**
   * Foto de um acompanhante que já existe. É o caso do que o painel criou: ele
   * ocupa a vaga mas não tem biometria, então não passa pelo formulário novo.
   */
  const sendPhotoFor = useCallback(
    async (companionId: string, file: File | null): Promise<void> => {
      const sessionId = getOnboardingSessionId();
      if (file === null || sessionId === null) return;
      setSubmitError(null);
      setShareFeedback(null);

      const base64 = await readCompanionPhoto(file);
      if (base64 === 'too_large' || base64 === 'invalid_type') {
        setPhotoError(base64);
        return;
      }

      setUploadingFor(companionId);
      try {
        const response = await api.onboarding.companionPhotoFor(companionId, {
          sessionId,
          base64Image: base64,
          consentConfirmed: true,
        });
        setRows((current) =>
          current.map((row) => (row.id === companionId ? response.companion : row)),
        );
        setProgress(response.companions);
        updateOnboardingGuestCompanions(response.companions);
      } catch (error) {
        setSubmitError(
          error instanceof ApiError
            ? error.message
            : 'Não foi possível enviar a foto. Tente novamente.',
        );
      } finally {
        setUploadingFor(null);
      }
    },
    [],
  );

  const finish = useCallback(() => {
    navigate('/success', successNavigationOptions);
  }, [navigate]);

  return {
    progress,
    rows,
    isLoading,
    loadError,
    reload,
    fullName,
    birthDate,
    document,
    email,
    nationality,
    editingId,
    editingDocumentMasked,
    removingId,
    hasPhoto: base64Image !== null,
    consentConfirmed,
    canSelfOnboard,
    photoError,
    submitError,
    shareFeedback,
    isSubmitting,
    canSubmitWithPhoto,
    canSubmitForSelfOnboarding,
    hasOpenSlot,
    uploadingFor,
    setFullName,
    setBirthDate,
    setDocument,
    setEmail,
    setNationality,
    setConsentConfirmed,
    startEdit,
    cancelEdit,
    saveEdit,
    removeCompanion,
    pickPhoto,
    submitWithPhoto,
    submitForSelfOnboarding,
    share,
    sendPhotoFor,
    finish,
  };
}
